/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */

import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Subject, Observable, merge, combineLatest, distinctUntilChanged } from 'rxjs';
import { filter, map, startWith, switchMap } from 'rxjs/operators';
import { GroupedTicket } from '@touchplan/ticket';
import { Side } from '@touchplan/common';
import { FeatureFlagManager } from 'src/app/services/FeatureFlagManager';
import { DynamicFlagTypes } from '@touchplan/feature-flags';

export interface IDockedTicketExternalData {
	isValid$: Observable<boolean>;
	isValidSync: () => boolean;
	isDirty$: Observable<boolean>;
	isDirtySync: () => boolean;
	ticket$: Observable<GroupedTicket>;
	closeEdit$: Observable<void>;
	trySave: () => void;
	applyWorkdaysToForm: (workdays: boolean[]) => void;
}

export interface IWorkdaysUiMetadata {
	liveWorkingDays: boolean[];
	ticketId: string;
	editCount: number;
	isActive: boolean;
}

@Injectable({ providedIn: 'root' })
export class TicketEditCommonService {
	private featureFlagManager = inject(FeatureFlagManager);
	private instancesCount = 1;

	//these lifecycle subjects objectively do not support the "multiple edit instances" use case
	private waitForRegistration$ = new Subject<void>();
	private _uiInitialized$ = new BehaviorSubject<boolean>(false);
	public uiInitialized$ = this._uiInitialized$.asObservable();

	public dockedTicketEnabled$: Observable<boolean> = this.featureFlagManager.getFlag$(
		DynamicFlagTypes.DOCKED_TICKET_IN_DESKTOP_ENABLED
	);

	//multiple edit dialogs capable, don't put much effort into it though
	private editInstances: Map<string, IDockedTicketExternalData> = new Map();
	private editInstancesCount$ = new BehaviorSubject(this.editInstances.size);

	public registerInstance(instance: IDockedTicketExternalData) {
		const instanceKey = 'ticket-edit- ' + this.instancesCount++;
		this.editInstances.set(instanceKey, instance);
		this.editInstancesCount$.next(this.editInstances.size);
		this.waitForRegistration$.next();
		this._uiInitialized$.next(true);
		return instanceKey;
	}
	public unregisterInstance(id: string) {
		this.editInstances.delete(id);
		this.editInstancesCount$.next(this.editInstances.size);
		if (this.editInstances.size === 0) {
			this._uiInitialized$.next(false);
		}
	}

	// assume one for now and return the first
	private getCurrentEditInstance(): IDockedTicketExternalData {
		const currentEdit = this.editInstances.values().next();
		if (currentEdit.done) {
			throw new Error('No current edit instance found');
		}
		return currentEdit.value;
	}

	//tbd how well this'll work if there's multiple edits open at once
	private waitForCurrentEditInstance$(): Observable<IDockedTicketExternalData> {
		return this.waitForRegistration$.pipe(
			map(() => this.getCurrentEditInstance())
			// take(1) // undecided if this should be a "once on page load" thing or not
		);
	}

	/**
	 * Returns an observable of a boolean indicating whether the current edit instance is valid or not.
	 */
	public isEditValid(): Observable<boolean> {
		return this.getCurrentEditInstance().isValid$;
	}

	/**
	 * Returns true if the current edit instance is valid.
	 */
	public isEditValidSync(): boolean {
		return this.getCurrentEditInstance().isValidSync();
	}

	/**
	 * Returns an observable indicating true when form is dirty or false when it is not dirty, ie no edits made.
	 */
	public isTicketFormDirty() {
		return this.uiInitialized$.pipe(
			filter((initialized) => initialized),
			switchMap((_) => {
				return this.getCurrentEditInstance().isDirty$;
			})
		);
	}

	/**
	 * Returns true when form is dirty or false when it is not dirty, ie no edits made yet.
	 */
	public isTicketFormDirtySync() {
		if (this.editInstances.size > 0) {
			return this.getCurrentEditInstance().isDirtySync();
		} else {
			return false;
		}
	}

	/**
	 * Calls try save on the current edit instance.
	 */
	public trySave() {
		this.getCurrentEditInstance().trySave();
	}

	/**
	 * Calls trySave if there is an edit instance open.
	 */
	public trySaveAndClose() {
		if (this.isTicketEditOpenSync()) {
			this.getCurrentEditInstance().trySave();
		}
	}

	public applyWorkdaysToForm(workdays: boolean[]) {
		this.getCurrentEditInstance().applyWorkdaysToForm(workdays);
	}

	/**
	 * Returns an observable returning a boolean indicating whether the edit ticket window is open or not
	 * by looking at the editInstance count.
	 */
	public isTicketEditOpen(): Observable<boolean> {
		return this.editInstancesCount$.pipe(map((value) => value > 0));
	}

	/**
	 * Returns a boolean indicating whether the edit ticket window is open or not by looking at the current
	 * edit instance count.
	 */
	public isTicketEditOpenSync(): boolean {
		return this.editInstances.size > 0;
	}

	// current inclination, curate everything in this service, never expose the full data
	public getWorkdaysUiMetadata(): Observable<IWorkdaysUiMetadata | null> {
		return this.waitForCurrentEditInstance$().pipe(
			// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7102
			switchMap((instance) => {
				// old edit's ticket observable emits null on close and the workdays canvas component
				// relies on that to clear. TBD if this stays, at all, here, or gets built into the new ticket$
				// NOTE - this only works if closed from new edit, non-ideal
				return merge(instance.ticket$, instance.closeEdit$.pipe(map(() => null)));
			}),
			// END PLACEHOLDER REPLACE AS PART OF TP-7102
			map((ticket) => {
				if (!ticket) {
					return null;
				}
				return {
					liveWorkingDays: ticket.view.liveWorkingDays,
					ticketId: ticket.$id,
					editCount: ticket.srcList.size,
					isActive: ticket.view.side !== Side.PULL,
				};
			})
		);
	}

	/**
	 * Returns an observable that sends true when the ticket form was closed and was dirty
	 * (ie some change saved)
	 * This is used to figure out when we need to create an Undo Item.  This was the basic logic used in
	 * the old ticket edit to signal this, but maybe we can do something better...
	 */
	public ticketEditClosedWithChanges() {
		return combineLatest({
			isDirty: this.isTicketFormDirty().pipe(startWith(false)),
			isEditOpen: this.isTicketEditOpen(),
		}).pipe(
			filter(({ isDirty, isEditOpen }) => isDirty && !isEditOpen),
			distinctUntilChanged(),
			map((_) => {
				return true;
			})
		);
	}
}

//current usage list (of old edit service)
// - canvas stuff (tbd)
//    - work days component in the canvas wants to push data to the edit data
// realisticaly it wants to be treated like user input, not system input. So the generic
// case might be something like... "want to bind external controls conceptually to the edit data"
// The sane way to do this is probably "hyper specific common service update methods"
// since this wants to be _fairly_ discouraged for most cases. Also worth considering if it should be
// registered _literally_ as a control
// - undo stuff (tbd)
// - ticketEditService.hideTicketAttachments(); (mostly as a "close opened dialogs" thing)
// - ticketEditService.getSingleEditTicket$(); could probably replace this with direct ticketList use
// - ticketEditService.ticketEditPanelIsDisplayed$; could probably replace this with direct ticketList use
// - "is edit valid" checks, sync and obserable
// - a bunch of floating tickets hackery
//   - ticketEditService.saveEditAndClose();
// - ticketEditService.ticketTemplate$
//   - used... as a weird way to see if edit is open??
// - externalSaveCustomFieldsRequest$
//   - either not used, or something being used as a bridge (cycle tickets emits to it, currently unknown if anything uses it)
//   - goes down a really stupid chain of observables to eventually call a method _inside_ the
// the custom fields list component that actually does the save. This is... extremely dumb. The new design
// has custom fields triggering a request save event out of docked ticket where it can call a service...
// this new design has yet to be implemented
// - ticketEditService.closeEditAndCheckDirty$
//   - either not used, or something being used as a bridge (ticket edit locations emits to it, currently unknown if anything uses it)
//   - looks like it's exclusively read by edit undo, don't fully understand why, has "timing vibes" though
// - logImplicitSave() - ...literally logged a canned save string when the form is dirty???
// - arbitrary queries of the current "groupedTicket state", sometimes observable, sometimes sync

// summary
// - the ability to know if edit is valid/ dirty (other form states might be useful)
// - the ability to know if edit is open
// - the ability to trigger a save / save and close externally
//   - saving of edit should also trigger a save of custom fields
// - essentially "arbitrary groupedTicket state" access, potentially with some helpers
//   - number being edited can be "isMulti$"

// musings
// - multistage init useful?
//   - some of the data is available before docked ticket is open and there's not necessarily
// clean hooks to wait for "init and loaded"
// - it's possible we may not even want to hook up the internal state and can rely 100% on the groupedTicket created before
// the only real exception is "validity"
